import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  withStyles,
  Typography,
  CircularProgress,
  Checkbox,
} from '@material-ui/core';
import { withI18n } from 'react-i18next';
import * as yup from 'yup';
import { Close } from '@material-ui/icons';
import { locale } from '@/utils/locale';
import {
  validatePhoneCode, changePhoneNumber, phoneNumberChangeFlow,
} from '@/redux/actions/phoneNumberVerify';
import { loadSummary, phoneNumberFlowSkip, setPhoneVerified } from '@/redux/actions/summary';
import {
  ERROR_CHANGE_PHONE_NUMBER,
  ERROR_VALIDATE_PHONE_CODE,
  RECEIVE_CHANGE_PHONE_NUMBER,
  RECEIVE_VALIDATE_PHONE_CODE,
} from '@/redux/actions/actionsTypes';
import { handleCoverPhoneNumber } from '@/utils/handlers/handleCoverPhoneNumber';
import { generateRecaptchaToken, loadReCaptchaPaaS } from '@/utils/Application/reCaptcha';
import PhoneCard from '@/components/PhoneVerify/PhoneCard';
import OutlineButton from '@/components/Buttons/OutlineButton';
import PrimaryButton from '@/components/Buttons/PrimaryButton';
import LinkButton from '@/components/Buttons/LinkButton';
import ChangePhoneNumber from '@/pages/PhoneVerify/ChangePhoneNumber';
import VerificationCode from '@/pages/PhoneVerify/VerificationCode';
import PhoneConfirmation from '@/pages/PhoneVerify/PhoneConfirmation';
import {
  changePhoneNumberTagManager,
  phoneNumberCodeValidTagManager,
  phoneNumberSendVerificationCodeTagManager,
  phoneNumberVerifySkipFlowTagManager,
} from '@/utils/ThirdParties/tagManager';
import isValidJsonString from '@/utils/Validators/isValidJsonString';
import styles from './styles';

import { queryParams } from '@/utils/Application/queryParams';
import { useHistory, useLocation } from 'react-router';

import { useParams } from 'react-router-dom';
import { countryCodesEnum } from './PhoneVerify.locale';
import { config } from '@/config';
import { Timer } from '../common/v1';
import { addSeconds, differenceInSeconds } from 'date-fns';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import {
  XRAY_ENABLED_VALIDATION_FILTER_WITH_PAGE_NAME,
} from '@/config/GrowthBook/constants';
import Countdown from '@/components/Labels/Countdown';
import { authActions, commonActions } from '@/redux/modules';

export const FORM_STEPS = {
  PHONE_CONFIRMATION: 'phoneConfirmation',
  CHANGE_PHONE_NUMBER: 'changePhoneNumber',
  VERIFICATION_CODE: 'verificationCode',
};

const PhoneVerify = ({ classes }) => {
  const history = useHistory();

  const [tokenRequestDiff, setTokenRequestDiff] = useState(0);

  const [countDownResend, setCountDownResend] = useState(tokenRequestDiff > 0
    ? tokenRequestDiff
    : 0);

  const [phoneCardForm, setPhoneCardForm] = useState(FORM_STEPS.PHONE_CONFIRMATION);
  const [showCountdown, setShowCountdown] = useState(false);
  const [phoneNumberAgreement, setPhoneNumberAgreement] = useState(false);
  const [values, setValues] = useState({
    codeInput: {
      value: undefined,
      error: '',
    },
    changePhoneNumberInput: {
      value: '',
      error: '',
    },
    password: {
      value: '',
      error: '',
    },
  });
  const params = useParams();
  const { changeFlow } = params;
  const countdownTime = 120;
  const loadingSend = useSelector(state => state.phoneNumberVerify.loadingSend);
  const { loadingSendToken } = useSelector(state => state.auth);
  const loadingValidate = useSelector(state => state.phoneNumberVerify.loadingValidate);
  const forceUrl = useSelector(state => state.phoneNumberVerify.forceUrl);
  const clientInfo = useSelector(state => state.summary);
  const phoneNumberCountry = useSelector(state => state.summary.phoneNumberCountry);
  const loadingSummary = useSelector(state => state.summary.loading);
  const featureTogglePhoneNumberFlowSkip = useSelector(state => state.featureToggles.toggles && state.featureToggles.toggles.portal && state.featureToggles.toggles.portal.phoneNumberFlowSkip);
  const userId = useSelector(state => state.summary.id);
  const dispatch = useDispatch();
  const { id: clientId, phoneNumber } = clientInfo;

  const lastPath = localStorage.getItem('lastPath');
  const lastPaths = isValidJsonString(lastPath) ? JSON.parse(lastPath) : [];
  const isEnabledNewFilter = useFeatureIsOn(XRAY_ENABLED_VALIDATION_FILTER_WITH_PAGE_NAME);
  const location = useLocation();

  const executeFilterPath = () => {
    if (isEnabledNewFilter) {
      const selectedLocation = queryParams(location.search, 'location');

      if (selectedLocation != null && selectedLocation !== '') {
        // Replace spaces with / to mount the url with slashes
        const locationWithSlashes = selectedLocation.replace(/\s/g, '/');

        // Parse all query params except 'location'
        const searchParams = new URLSearchParams(location.search);
        searchParams.delete('location');

        // Build the new URL: /{location}?{other params}
        const otherParams = searchParams.toString();
        const newLocation = otherParams ? `/${locationWithSlashes}?${otherParams}` : `/${locationWithSlashes}`;

        return { id: userId, location: newLocation };
      }
    }
    return lastPaths ? lastPaths.filter(item => item.id === userId)[0] : [];
  };

  const routeToRedirect = locale('routes.home');
  const pathFilter = executeFilterPath();
  const isValidPath = pathFilter && pathFilter.location && pathFilter.id && pathFilter.id === userId;
  const closePath = isValidPath ? pathFilter.location : routeToRedirect;

  const [newPhone, setNewPhone] = useState(null);
  const countriesCode = useSelector(state => state.countryPhoneCodes.countryCode);
  const [countryCode, setCountryCode] = useState(phoneNumberCountry);

  const manipulateLocalStorageTokenRequested = useCallback(() => {
    const tokenRequested = localStorage.getItem('tokenRequested');
    const tokenDiff = differenceInSeconds(new Date(tokenRequested), new Date());

    if (tokenRequested) {
      if (tokenDiff > 0) {
        setTokenRequestDiff(tokenDiff);
        setCountDownResend(tokenDiff);
      }

      if (tokenDiff <= 0) {
        setTokenRequestDiff(0);
        setCountDownResend(0);
        localStorage.removeItem('tokenRequested');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneCardForm]);

  useEffect(() => {
    manipulateLocalStorageTokenRequested();
  }, [manipulateLocalStorageTokenRequested]);

  useEffect(() => {
    if (!closePath) {
      manipulateLocalStorageTokenRequested();
      setPhoneCardForm(FORM_STEPS.CHANGE_PHONE_NUMBER);
    }
  }, [phoneNumber, setPhoneCardForm, closePath, manipulateLocalStorageTokenRequested]);

  useEffect(() => {
    if (changeFlow === 'alterar') {
      manipulateLocalStorageTokenRequested();
      setPhoneCardForm(FORM_STEPS.CHANGE_PHONE_NUMBER);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeFlow]);

  useEffect(() => {
    loadReCaptchaPaaS();
  }, []);

  const optionsValidate = {
    abortEarly: false,
  };
  const schemaPhone = yup.object().shape({
    changePhoneNumberInput: yup
      .string()
      .min(6)
      .max(15)
      .required('required'),
    password: yup
      .string()
      .min(6)
      .required('required'),
  });

  const schemaCode = yup.object().shape({
    codeInput: yup
      .number()
      .typeError()
      .required('required'),
  });

  const updateField = (event) => {
    const { name } = event.target;

    if (name === 'countryCode') {
      setCountryCode(event.target.value);
    }

    if (name !== 'countryCode') {
      const newValues = {
        ...values,
        [event.target.name]: {
          ...values[event.target.name],
          value: event.target.value,
          error: '',
        },
      };

      setValues(newValues);
    }
  };

  const closePhoneFlow = () => {
    dispatch(phoneNumberFlowSkip(clientId));
    dispatch(phoneNumberChangeFlow(false));
    phoneNumberVerifySkipFlowTagManager();
  };

  const setNewPhoneDispatch = () => {
    const clientPhoneNumberNewClear = phoneNumber.replace(/\D/g, '');
    const newClientPhoneNumberNewClear = values.changePhoneNumberInput.value.replace(/\D/g, '');

    dispatch(changePhoneNumber({
      clientId,
      clientPhoneNumberNew: newClientPhoneNumberNewClear || clientPhoneNumberNewClear,
      countryCode,
      clientPhoneAgreement: phoneNumberAgreement,
    })).then((response) => {
      if (response.type === RECEIVE_CHANGE_PHONE_NUMBER) {
        dispatch(loadSummary());
        changePhoneNumberTagManager();

        dispatch(commonActions.notifications.set({
          label: locale('phoneVerify.feedback.changePhoneNumberSuccess'),
          type: 'success',
        }));
        dispatch(setPhoneVerified(true));
        closePhoneFlow();
        history.push(closePath);
      } else if (response.type === ERROR_CHANGE_PHONE_NUMBER) {
        dispatch(commonActions.notifications.set({
          label: locale('phoneVerify.feedback.changePhoneNumberError'),
          type: 'error',
        }));
      }
      setShowCountdown(true);
    });
  };

  const handleSendPhoneCode = async ({ clientPhoneNumberNewClear }) => {
    const dial = countriesCode[countryCode].code;
    let phoneNumber = clientPhoneNumberNewClear;
    const recaptchaToken = await generateRecaptchaToken(config.RECAPTCHA_PAAS, 'changePhoneNumber');

    if (!phoneNumber) {
      phoneNumber = newPhone || null;
    }

    if (countDownResend === 0) {
      dispatch(authActions.user.phoneToken.send({
        clientId,
        password: values.password.value,
        countryCode: dial,
        phoneNumber,
        clientPhoneAgreement: phoneNumberAgreement,
        recaptchaToken,
        setVerificationStep: () => {
          localStorage.setItem('tokenRequested', addSeconds(new Date(), countdownTime));
          setPhoneCardForm(FORM_STEPS.VERIFICATION_CODE);
          phoneNumberSendVerificationCodeTagManager();
        },
        setPendingCounter: (pendingAmount) => {
          if (pendingAmount) {
            const tokenDiff = differenceInSeconds(new Date(pendingAmount), new Date());
            setCountDownResend(tokenDiff);
            localStorage.setItem('tokenRequested', new Date(pendingAmount));
          } else {
            setCountDownResend(countdownTime);
            localStorage.setItem('tokenRequested', addSeconds(new Date(), countdownTime));
          }
        },
      }));
    }
  };

  const checkChangePhoneValidations = (clientPhoneNumberNew, action) => {
    const clientPhoneNumberNewClear = clientPhoneNumberNew.replace(/\D/g, '');

    schemaPhone.validate({ changePhoneNumberInput: clientPhoneNumberNewClear, password: values.password.value }, optionsValidate).then(() => {
      if (action === 'formSubmit') {
        setNewPhone(clientPhoneNumberNewClear);
        handleSendPhoneCode({ clientPhoneNumberNewClear });
      }
    }).catch((validationError) => {
      validationError && validationError.inner.forEach((error) => {
        let errorMessage;
        switch (error.type) {
          case 'required':
            errorMessage = locale('phoneVerify.errors.requiredCode');
            break;
          case 'min':
            errorMessage = locale('phoneVerify.errors.passwordMinimumCharacters');
            break;
          default:
            errorMessage = locale('phoneVerify.errors.invalidPhoneNumber');
            break;
        }
        setValues(prevValues => ({
          ...prevValues,
          [error.path]: {
            ...prevValues[error.path],
            error: errorMessage,
          },
        }));
      });
    });
  };

  const checkCodeValidations = (verificationCode, action) => {
    schemaCode.validate({ codeInput: verificationCode }, optionsValidate).then(() => {
      if (action === 'formSubmit') {
        dispatch(validatePhoneCode(
          verificationCode,
        )).then((response) => {
          if (response.type === RECEIVE_VALIDATE_PHONE_CODE) {
            if (values.changePhoneNumberInput.values && values.password.values) {
              setNewPhoneDispatch();
            } else {
              dispatch(commonActions.notifications.set({
                label: locale('phoneVerify.feedback.changePhoneNumberSuccess'),
                type: 'success',
              }));
              dispatch(setPhoneVerified(true));
              closePhoneFlow();
              history.push(closePath);
            }
            phoneNumberCodeValidTagManager();
          } else if (response && response.data && response.data.response.data.data === 'Token not found.') {
            dispatch(commonActions.notifications.set({
              label: locale('phoneVerify.feedback.invalidToken'),
              type: 'error',
            }));
          } else if (response.type === ERROR_VALIDATE_PHONE_CODE) {
            dispatch(commonActions.notifications.set({
              label: locale('phoneVerify.feedback.validateCodeError'),
              type: 'error',
            }));
          }
          setShowCountdown(true);
        });
      }
    }).catch((validationError) => {
      validationError && validationError.inner.forEach((error) => {
        setValues(prevValues => ({
          ...prevValues,
          [error.path]: {
            ...prevValues[error.path],
            error: error.type === 'required'
              ? locale('phoneVerify.errors.requiredCode')
              : locale('phoneVerify.errors.invalidCode'),
          },
        }));
      });
    });
  };

  const handleChangePhoneNumber = (data) => {
    data.preventDefault();
    const selectedCountryCode = data.target.selectCountryCode.value;
    setCountryCode(selectedCountryCode);

    checkChangePhoneValidations(values.changePhoneNumberInput.value, 'formSubmit');
  };

  const handleValidateCode = (event) => {
    event.preventDefault();
    checkCodeValidations(values.codeInput.value, 'formSubmit');
  };

  const handleShowCountdown = (show) => {
    setShowCountdown(show);
  };
  const handleCancel = () => {
    if (history.length) {
      dispatch(phoneNumberChangeFlow(false, forceUrl));
      history.push(forceUrl);
    }

    if (!history.length) {
      manipulateLocalStorageTokenRequested();
      setPhoneCardForm(FORM_STEPS.PHONE_CONFIRMATION);
    }
  };

  const handleShowChangePhoneNumberForm = () => {
    manipulateLocalStorageTokenRequested();
    setPhoneCardForm(FORM_STEPS.CHANGE_PHONE_NUMBER);
  };

  const handleValidateDdi = useCallback(() => {
    if (countryCode === countryCodesEnum.SINGAPORE) {
      setValues({
        ...values,
        changePhoneNumberInput: {
          ...values.changePhoneNumberInput,
          error: locale('phoneVerify.errors.countryCode'),
        },
      });
    } else {
      setValues({
        ...values,
        changePhoneNumberInput: {
          value: '',
          error: '',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode]);

  useEffect(() => {
    handleValidateDdi();
  }, [handleValidateDdi]);

  const canSendForm = useCallback(() => {
    const hasErros = values.codeInput.error.length || values.changePhoneNumberInput.error.length || values.password.error.length || countryCode === countryCodesEnum.SINGAPORE;
    const hasEmptyFields = !values.changePhoneNumberInput.value || !values.password.value;
    const cantContinue = hasErros || hasEmptyFields;

    return cantContinue;
  }, [values, countryCode]);

  const phoneCardContent = {
    changePhoneNumber: {
      title: phoneNumber ? locale('phoneVerify.changePhoneNumber.title') : locale('phoneVerify.emptyPhoneNumber.title'),
      subtitle: phoneNumber ? locale('phoneVerify.changePhoneNumber.subtitle') : locale('phoneVerify.emptyPhoneNumber.subtitle'),
    },

    verificationCode: {
      title: locale('phoneVerify.verifyCodeForm.title'),
      subtitle: locale('phoneVerify.verifyCodeForm.subtitle')(handleCoverPhoneNumber({
        phoneNumber: newPhone || phoneNumber,
        phoneNumberCountry,
      })),
    },

    phoneConfirmation: {
      title: locale('phoneVerify.title'),
      subtitle: locale('phoneVerify.subtitle'),
    },
  };

  const sendCodeLabel = loadingSend ? locale('phoneVerify.buttons.resendingLoading') : locale('phoneVerify.buttons.resend');

  const phoneCardChild = {
    changePhoneNumber: (
      <div className={classes.contentWrapper}>
        <ChangePhoneNumber
          handleChangePhoneNumber={handleChangePhoneNumber}
          phoneNumberCountry={phoneNumberCountry}
          handleCancel={handleCancel}
          loadingChange={loadingSendToken}
          updateField={updateField}
          values={values}
          phoneNumber={phoneNumber}
          passwordDescription={locale('phoneVerify.changePhoneNumber.passwordDescription')}
          passwordLabel={locale('phoneVerify.changePhoneNumber.passwordLabel')}
          countriesCode={countriesCode}
          canSendForm={canSendForm}
          countDownResend={countDownResend}
          setCountDownResend={setCountDownResend}
        />
      </div>
    ),
    verificationCode: (
      <div className={classes.phoneContentWrapper}>
        <VerificationCode
          handleValidateCode={handleValidateCode}
          loadingValidate={loadingValidate}
          updateField={updateField}
          values={values}
        />
        <div className={classes.footer}>
          {showCountdown ? (
            <>
              <Typography>
                {locale('phoneVerify.codeNotRecived')}
              </Typography>
              <Typography className={classes.countdownText}>
                {locale('phoneVerify.resendText')}
                <span className={classes.countdown}>
                  <Countdown countdownTime={countdownTime} handleShowCountdown={handleShowCountdown} />
                </span>
              </Typography>
            </>
          ) : (
            <div className={classes.wrapperSendCodeBoxLinks}>
              <Typography>{locale('phoneVerify.codeNotRecived')}</Typography>
              <OutlineButton
                className={classes.button}
                onClick={handleShowChangePhoneNumberForm}
                disabled={loadingSend}
              >
                {locale('phoneVerify.buttons.changePhoneNumber')}
              </OutlineButton>
              <PrimaryButton
                className={classes.button}
                onClick={handleSendPhoneCode}
                disabled={loadingSend || loadingSendToken || countDownResend > 0}
              >
                {countDownResend > 0
                  && <Timer startSecound={countDownResend} displayHour={false} onFinish={() => setCountDownResend(0)} />
                }

                {(loadingSend || loadingSendToken) && <CircularProgress className={classes.loading} size={20} />}
                {countDownResend <= 0 && sendCodeLabel}
              </PrimaryButton>
            </div>
          )}
          {featureTogglePhoneNumberFlowSkip && (
            <div className={classes.validateLaterWrapper}>
              <LinkButton
                to={closePath}
                onClick={() => closePhoneFlow()}
                addLocationSearch
              >
                {locale('phoneVerify.buttons.validateLater')}
              </LinkButton>
            </div>
          )}
        </div>
      </div>
    ),
    phoneConfirmation: (
      <div className={classes.phoneVerifyContainer}>
        <PhoneConfirmation
          phoneNumber={handleCoverPhoneNumber({
            phoneNumber: newPhone || phoneNumber,
            phoneNumberCountry,
          })}
          handleSendPhoneCode={handleSendPhoneCode}
          setPhoneCardForm={setPhoneCardForm}
          loadingSend={loadingSendToken}
          loadingSummary={loadingSummary}
          countDownResend={countDownResend}
          setCountDownResend={setCountDownResend}
        />

        <Typography>
          <Checkbox
            checked={phoneNumberAgreement}
            onChange={() => setPhoneNumberAgreement(!phoneNumberAgreement)}
          />
          {locale('phoneVerify.phoneAgreement')}
        </Typography>

        {featureTogglePhoneNumberFlowSkip && (
          <div className={classes.validateLaterWrapper}>
            <LinkButton
              to={closePath}
              onClick={() => closePhoneFlow()}
              addLocationSearch
            >
              {locale('phoneVerify.buttons.validateLater')}
            </LinkButton>
          </div>
        )}
      </div>
    ),
  };

  return (
    <div className={classes.main} data-id={phoneCardForm}>
      <PhoneCard
        title={phoneCardContent[phoneCardForm].title}
        subtitle={phoneCardContent[phoneCardForm].subtitle}
      >
        {phoneCardChild[phoneCardForm]}
        {featureTogglePhoneNumberFlowSkip && (
          <LinkButton
            data-id={`close-${phoneCardForm}`}
            to={closePath}
            className={classes.link}
            onClick={() => closePhoneFlow()}
            addLocationSearch
          >
            <Close />
          </LinkButton>
        )}
      </PhoneCard>
    </div>
  );
};

export default withI18n()(withStyles(styles)(PhoneVerify));
